import { Suspense, lazy, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { getPageBySlug } from '../lib/strapi';
import { applySeoMetadata } from '../lib/seo';
import { useGlobal } from '../context/GlobalContext';
import { isEnglishLocale } from '../lib/locale';
import {
  getHomeDeeplinkPath,
  getHomeSlugForLocale,
  getLocalizedPagePath,
  getLocaleForLocalizedSlug,
  isHomeSlug,
  localeMatchesLocalizedSlug,
  resolveSlugForLocale,
} from '../lib/localizedSlugs';
import NotFound from './NotFound';
import Hero from '../components/Hero/Hero';
import Description from '../components/Description/Description';
import DescriptionHome from '../components/DescriptionHome/DescriptionHome';
import DescriptionModelo from '../components/DescriptionModelo/DescriptionModelo';
import ScrollCta from '../components/ScrollCta/ScrollCta';
import EspacioVacio from '../components/EspacioVacio/EspacioVacio';
import WhatsAppContactCta from '../components/WhatsAppContactCta/WhatsAppContactCta';
import './DynamicPage.css';

const Gallery = lazy(() => import('../components/Gallery/Gallery'));
const GalleryInfo = lazy(() => import('../components/GalleryInfo/GalleryInfo'));
const Location = lazy(() => import('../components/Location/Location'));
const Floorplan = lazy(() => import('../components/Floorplan/Floorplan'));
const ModelHeader = lazy(() => import('../components/ModelHeader/ModelHeader'));
const MainFeatures = lazy(() => import('../components/MainFeatures/MainFeatures'));
const SecondaryFeatures = lazy(() => import('../components/SecondaryFeatures/SecondaryFeatures'));
const AvailabilityList = lazy(() => import('../components/AvailabilityList/AvailabilityList'));
const Cotizador = lazy(() => import('../components/Cotizador/Cotizador'));
const LegalText = lazy(() => import('../components/LegalText/LegalText'));
const Faq = lazy(() => import('../components/Faq/Faq'));
const Tipologias = lazy(() => import('../components/Tipologias/Tipologias'));

// Normaliza un slug para comparar (lowercase, sin /, sin querystring).
const normalizeSlug = (raw) =>
  String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/^\/+/, '')
    .replace(/\?.*$/, '');

// Es el slug un deepLink/seccion del Hero 360 del home?
const isHomeAliasSlug = (homeData, slug) => {
  const target = normalizeSlug(slug);
  if (!target) return false;

  const sections = Array.isArray(homeData?.sections) ? homeData.sections : [];

  for (const section of sections) {
    if (section?.__component === 'blocks.hero-iframe360') {
      const deepLinks = Array.isArray(section.deepLinks) ? section.deepLinks : [];
      if (deepLinks.some((link) => normalizeSlug(link?.label) === target)) {
        return true;
      }
    }
    if (section?.__component === 'blocks.hero-secciones') {
      const secciones = Array.isArray(section.secciones) ? section.secciones : [];
      if (secciones.some((s) => normalizeSlug(s?.slug) === target)) {
        return true;
      }
    }
  }

  return false;
};

const renderDeferred = (content) => (
  <Suspense fallback={null}>
    {content}
  </Suspense>
);

const DynamicPage = () => {
  const { slug = '' } = useParams();
  const location = useLocation();
  const {
    locale,
    changeLocale,
    setPageOverrides,
    setLocalizedPagePaths,
    setHomeLogoPath,
    companyInfo,
    spanishLocale,
    englishLocale,
  } = useGlobal();
  const [page, setPage] = useState(null);
  const [cotizadorState, setCotizadorState] = useState({
    isOpen: false,
    itemId: null,
  });
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchWithRetry = async (targetSlug, targetLocale) => {
      const delays = [400, 1200];
      let lastError = null;

      for (let attempt = 0; attempt <= delays.length; attempt += 1) {
        try {
          return await getPageBySlug(targetSlug, targetLocale);
        } catch (err) {
          lastError = err;
          if (attempt < delays.length) {
            await new Promise((resolve) => setTimeout(resolve, delays[attempt]));
          }
        }
      }

      throw lastError;
    };

    const fetchPage = async () => {
      const isRootHome = !slug;
      const slugLocale = isRootHome ? null : getLocaleForLocalizedSlug(slug);
      if (slugLocale && !localeMatchesLocalizedSlug(slug, locale)) {
        changeLocale(slugLocale);
        return;
      }

      setLoading(true);
      setNotFound(false);
      setFetchError(false);
      setPage(null);

      try {
        const resolvedSlug = resolveSlugForLocale(slug, locale);
        let data = await fetchWithRetry(resolvedSlug, locale);

        // Si el slug no existe en Strapi, podria ser un "alias de seccion"
        // del home (deepLink del bloque hero-iframe360).
        if (!data && !isHomeSlug(slug)) {
          const homeData = await fetchWithRetry(getHomeSlugForLocale(locale), locale);
          if (homeData && isHomeAliasSlug(homeData, slug)) {
            data = homeData;
          }
        }

        if (cancelled) return;

        if (!data) {
          setNotFound(true);
        } else {
          setPage(data);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching page (transient):', err);
        setFetchError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchPage();

    return () => {
      cancelled = true;
    };
  }, [slug, locale, changeLocale, location.search]);

  useEffect(() => {
    if (page) {
      applySeoMetadata({
        page,
        companyInfo,
        locale,
        requestedSlug: resolveSlugForLocale(slug, locale),
      });
      return;
    }

    if (notFound) {
      applySeoMetadata({
        companyInfo,
        locale,
        noIndex: true,
        requestedSlug: resolveSlugForLocale(slug, locale),
        seo: {
          metaTitle: `404 | ${companyInfo?.companyName || 'Koppi'}`,
          metaDescription: 'Pagina no encontrada',
        },
      });
      return;
    }

    if (fetchError && companyInfo) {
      applySeoMetadata({
        companyInfo,
        locale,
        noIndex: false,
        requestedSlug: resolveSlugForLocale(slug, locale),
        seo: {},
      });
    }
  }, [page, notFound, fetchError, companyInfo, locale, slug]);

  // Sync per-page UI overrides (hide header/footer/chatbot) with global state.
  useEffect(() => {
    setPageOverrides({
      hideHeader: Boolean(page?.hideHeader),
      hideFooter: Boolean(page?.hideFooter),
      hideChatbot: Boolean(page?.hideChatbot),
    });
    return () => setPageOverrides({ hideHeader: false, hideFooter: false, hideChatbot: false });
  }, [page?.hideHeader, page?.hideFooter, page?.hideChatbot, setPageOverrides]);

  useEffect(() => {
    if (!page) {
      setLocalizedPagePaths({});
      return undefined;
    }

    setLocalizedPagePaths({
      es: getLocalizedPagePath(page, spanishLocale),
      en: getLocalizedPagePath(page, englishLocale),
    });

    return () => setLocalizedPagePaths({});
  }, [page, spanishLocale, englishLocale, setLocalizedPagePaths]);

  useEffect(() => {
    const pageSlug = page?.slug || page?.attributes?.slug;
    if (!pageSlug || !isHomeSlug(pageSlug)) return;

    setHomeLogoPath(getHomeDeeplinkPath(page));
  }, [page, setHomeLogoPath]);

  if (notFound) {
    return <NotFound />;
  }

  if (fetchError) {
    const isEn = isEnglishLocale(locale);

    return (
      <div className="dynamic-page dynamic-page--error">
        <div className="dynamic-page__error-card">
          <p className="dynamic-page__error-eyebrow">
            {isEn ? 'Temporary connection issue' : 'Problema temporal de conexion'}
          </p>
          <h1 className="dynamic-page__error-title">
            {isEn ? 'We are still loading the content' : 'Estamos cargando el contenido'}
          </h1>
          <p className="dynamic-page__error-message">
            {isEn
              ? 'If the problem persists, reload the page in a few seconds.'
              : 'Si el problema persiste, recarga la pagina en unos segundos.'}
          </p>
        </div>
      </div>
    );
  }

  const sections = page
    ? (page.sections || []).filter((s) => s.__component !== 'blocks.hero-secciones')
    : [];

  const cotizadorConfig = sections.find((s) => s.__component === 'blocks.cotizador') || null;
  const getItemKey = (entry) => entry?.id ?? entry?.documentId ?? null;
  const activeCotizadorItem = cotizadorState.itemId
    ? ((page?.items || []).find((entry) => getItemKey(entry) === cotizadorState.itemId) || null)
    : null;
  const isCotizadorOpen = cotizadorState.isOpen && Boolean(activeCotizadorItem);

  const heroSeccionesBlock = (page?.sections || []).find(
    (s) => s.__component === 'blocks.hero-secciones'
  ) || null;
  const heroSecciones = heroSeccionesBlock?.secciones || [];
  const shouldDeferInternalOnlyBlocks = !isHomeSlug(page?.slug || slug);

  const isDescription = (section) => section?.__component === 'blocks.description';
  const isHomeDescription = (section) => section?.__component === 'blocks.descripcion-home';
  const isModelDescription = (section) => section?.__component === 'blocks.descripcion-modelo';
  const isAnyDescription = (section) => (
    isDescription(section)
    || isHomeDescription(section)
    || isModelDescription(section)
  );
  const descriptionHasBackgroundImage = (section) => (
    isHomeDescription(section)
    && Boolean(section?.backgroundImage?.url)
  );
  const getSectionKey = (section, index, prefix = 'section') => (
    [prefix, section?.__component || 'unknown', section?.id ?? 'noid', index].join('-')
  );
  const isHalfWidthDescription = (section) => {
    if (isModelDescription(section)) return true;
    if (isDescription(section)) return Boolean(section?.halfWidth);

    if (!isHomeDescription(section)) return false;

    if (typeof section?.halfWidth === 'boolean') {
      return section.halfWidth;
    }

    return !descriptionHasBackgroundImage(section);
  };
  const isLastDescriptionRow = (section, index) => {
    if (!isAnyDescription(section)) return false;

    const lastIndex = sections.length - 1;
    const secondLastIndex = sections.length - 2;

    if (index === lastIndex) {
      return true;
    }

    return (
      isHalfWidthDescription(section)
      && index === secondLastIndex
      && isHalfWidthDescription(sections[lastIndex])
    );
  };

  const renderSection = (section, index, anchorId, isAttached) => {
    switch (section.__component) {
      case 'blocks.scroll-cta':
        return (
          <ScrollCta
            key={`scroll-cta-${section.id || index}`}
            text={section.label}
            targetId={anchorId}
            isAttached={isAttached}
            textFontSize={section.textFontSize}
            arrowWidth={section.arrowWidth}
            arrowHeight={section.arrowHeight}
            buttonPadding={section.buttonPadding}
          />
        );
      case 'blocks.espacio-vacio':
        return (
          <EspacioVacio
            key={`espacio-vacio-${section.id || index}`}
            height={section.height}
          />
        );
      case 'blocks.description':
      {
        const isMiddle = isAnyDescription(sections[index - 1]) && isAnyDescription(sections[index + 1]);
        return (
          <Description
            key={`description-${section.id || index}`}
            data={section}
            isMiddle={isMiddle}
            anchorId={anchorId}
          />
        );
      }
      case 'blocks.descripcion-home': {
        const isMiddle = isAnyDescription(sections[index - 1]) && isAnyDescription(sections[index + 1]);
        return (
          <DescriptionHome
            key={`description-home-${section.id || index}`}
            data={section}
            isMiddle={isMiddle}
            anchorId={anchorId}
          />
        );
      }
      case 'blocks.descripcion-modelo': {
        const isMiddle = isAnyDescription(sections[index - 1]) && isAnyDescription(sections[index + 1]);
        return (
          <DescriptionModelo
            key={`description-modelo-${section.id || index}`}
            data={section}
            isMiddle={isMiddle}
            anchorId={anchorId}
          />
        );
      }
      case 'blocks.location':
        return renderDeferred(
          <Location
            key={`location-${section.id || index}`}
            data={section}
          />
        );
      case 'blocks.floorplan':
        return renderDeferred(
          <Floorplan
            key={`floorplan-${section.id || index}`}
            data={section}
          />
        );
      case 'blocks.gallery':
        return shouldDeferInternalOnlyBlocks ? renderDeferred(
          <Gallery
            key={`gallery-${section.id || index}`}
            data={section}
          />
        ) : (
          <Gallery
            key={`gallery-${section.id || index}`}
            data={section}
          />
        );
      case 'blocks.gallery-info':
        return shouldDeferInternalOnlyBlocks ? renderDeferred(
          <GalleryInfo
            key={`gallery-info-${section.id || index}`}
            data={section}
          />
        ) : (
          <GalleryInfo
            key={`gallery-info-${section.id || index}`}
            data={section}
          />
        );
      case 'blocks.contact-form':
        return <WhatsAppContactCta key={`whatsapp-contact-${section.id || index}`} id={anchorId} />;
      case 'blocks.hero-image':
      case 'blocks.hero-video':
      case 'blocks.hero-gallery':
      case 'blocks.hero-iframe360':
        return (
          <div key={`hero-${section.id || index}`} className="dynamic-page__section dynamic-page__section--full">
            <Hero data={[section]} secciones={heroSecciones} />
          </div>
        );
      case 'blocks.hero-secciones':
        return null;
      case 'blocks.popup-fullscreen':
        return null;
      case 'blocks.model-header':
        return renderDeferred(
          <ModelHeader
            key={`model-header-${section.id || index}`}
            data={section}
            items={page?.items || []}
          />
        );
      case 'blocks.main-features':
        return renderDeferred(
          <MainFeatures
            key={`main-features-${section.id || index}`}
            data={section}
          />
        );
      case 'blocks.secondary-features':
        return renderDeferred(
          <SecondaryFeatures
            key={`secondary-features-${section.id || index}`}
            data={section}
          />
        );
      case 'blocks.availability-list':
        return renderDeferred(
          <AvailabilityList
            key={`availability-list-${section.id || index}`}
            data={section}
            items={page?.items || []}
            pageTitulo={page?.titulo || ''}
            cotizadorConfig={cotizadorConfig}
            onOpenCotizador={(item) => setCotizadorState({ isOpen: true, itemId: getItemKey(item) })}
          />
        );
      case 'blocks.cotizador':
        return renderDeferred(
          <Cotizador
            key={`cotizador-${section.id || index}`}
            data={section}
            item={activeCotizadorItem}
            isOpen={isCotizadorOpen}
            onClose={() => setCotizadorState({ isOpen: false, itemId: null })}
            page={page}
            source={resolveSlugForLocale(slug, locale)}
          />
        );
      case 'blocks.tipologias':
        return shouldDeferInternalOnlyBlocks ? renderDeferred(
          <Tipologias
            key={`tipologias-${section.id || index}`}
            data={section}
            items={page?.items || []}
          />
        ) : (
          <Tipologias
            key={`tipologias-${section.id || index}`}
            data={section}
            items={page?.items || []}
          />
        );
      case 'blocks.legal-text':
        return renderDeferred(
          <LegalText
            key={`legal-text-${section.id || index}`}
            data={section}
          />
        );
      case 'blocks.faq':
        return renderDeferred(
          <Faq
            key={`faq-${section.id || index}`}
            data={section}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={`dynamic-page${page?.hideHeader ? ' dynamic-page--no-header' : ''}${isHomeSlug(slug) ? ' dynamic-page--home' : ''}`}>
      {loading && !notFound && !fetchError && (
        <div className="dynamic-page__overlay">
          <div className="dynamic-page__spinner" />
        </div>
      )}
      {page && page.hero && <Hero data={page.hero} secciones={heroSecciones} />}

      <div className="dynamic-page__content">
        {page && page.titulo && !page.hero && (
          <header className="dynamic-page__header">
            <h1>{page.titulo}</h1>
            {page.subtitulo && <p>{page.subtitulo}</p>}
          </header>
        )}

        {sections.length > 0 && (
          <div className="dynamic-page__sections">
            {(() => {
              let descriptionAnchorAssigned = false;
              const descriptionAnchorId = 'descripcion-imagen';
              const scrollCtaEndId = 'scroll-cta-end';
              const renderedIndices = new Set();

              return sections.map((section, index) => {
                if (renderedIndices.has(index)) return null;

                const hasBackgroundImage = descriptionHasBackgroundImage(section);
                const isDescriptionSection = isAnyDescription(section);
                const prevSection = sections[index - 1];
                const nextSection = sections[index + 1];
                const nextNextSection = sections[index + 2];
                const isAfterHero =
                  section.__component === 'blocks.scroll-cta' &&
                  (prevSection?.__component?.startsWith('blocks.hero-') || (index === 0 && page.hero));

                let anchorId = null;
                if (!descriptionAnchorAssigned && isDescriptionSection && hasBackgroundImage) {
                  anchorId = descriptionAnchorId;
                  descriptionAnchorAssigned = true;
                }

                const targetId = section.__component === 'blocks.scroll-cta' ? scrollCtaEndId : anchorId;

                const isCardDescription = isHalfWidthDescription(section);

                const isMainFeatures = section.__component === 'blocks.main-features';
                const isDescNoFondo = (s) => isModelDescription(s);
                const nextIsDescNoFondo = isDescNoFondo(nextSection);
                const nextIsSecondary = nextSection?.__component === 'blocks.secondary-features';
                const nextNextIsSecondary = nextNextSection?.__component === 'blocks.secondary-features';

                if (isMainFeatures && nextIsDescNoFondo && nextNextIsSecondary) {
                  renderedIndices.add(index);
                  renderedIndices.add(index + 1);
                  renderedIndices.add(index + 2);
                  return (
                    <div key={getSectionKey(section, index, 'features-group')} className="dynamic-page__features-row">
                      <div className="dynamic-page__features-left">
                        {renderSection(section, index, null, false)}
                        {renderSection(nextSection, index + 1, null, false)}
                      </div>
                      <div className="dynamic-page__features-right">
                        {renderSection(nextNextSection, index + 2, null, false)}
                      </div>
                    </div>
                  );
                }

                if (isDescNoFondo(section) && nextIsSecondary) {
                  renderedIndices.add(index);
                  renderedIndices.add(index + 1);
                  return (
                    <div key={getSectionKey(section, index, 'features-group')} className="dynamic-page__features-row">
                      <div className="dynamic-page__features-left">
                        {renderSection(section, index, null, false)}
                      </div>
                      <div className="dynamic-page__features-right">
                        {renderSection(nextSection, index + 1, null, false)}
                      </div>
                    </div>
                  );
                }

                const isFullWidth = section.__component === 'blocks.availability-list'
                  || section.__component === 'blocks.contact-form'
                  || section.__component === 'blocks.floorplan';

                if (isFullWidth) {
                  return (
                    <div key={getSectionKey(section, index)} className="dynamic-page__section-wrapper dynamic-page__section-wrapper--full-width">
                      {renderSection(section, index, targetId, isAfterHero)}
                    </div>
                  );
                }

                const wrapperClass = [
                  'dynamic-page__section-wrapper',
                  isCardDescription ? 'dynamic-page__section-wrapper--card' : null,
                  isLastDescriptionRow(section, index)
                    ? 'dynamic-page__section-wrapper--description-last'
                    : null,
                ].filter(Boolean).join(' ');

                return (
                  <div key={getSectionKey(section, index)} className={wrapperClass}>
                    {renderSection(section, index, targetId, isAfterHero)}
                  </div>
                );
              }).filter(Boolean);
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default DynamicPage;
